import type {
  ApiErrorBody,
  AttributionRequest,
  AttributionResponse,
  CatalogResponse,
  CheckoutResponse,
  ConfirmCheckoutResponse,
  CreateGenerationRequest,
  GenerationResponse,
  GenerationListResponse,
  HealthResponse,
  MeResponse,
  MediaUploadResponse,
  PublicConfigResponse,
  CheckoutUiMode,
  PaymentOffersResponse,
  PaymentPricesResponse,
  PortalReturnResponse,
  WelcomeBonusResponse,
} from '@/types/api'
import { accessToken } from '@/services/session'

const baseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1'

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly requestId?: string,
    readonly code?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// SDK token restoration can outlive a page just like fetch. Stop awaiting it on cancellation.
export function withSignal<T>(promise: Promise<T>, signal?: AbortSignal): Promise<T> {
  if (!signal) return promise
  return new Promise<T>((resolve, reject) => {
    const abort = () => reject(signal.reason ?? new DOMException('Aborted', 'AbortError'))
    if (signal.aborted) abort()
    else signal.addEventListener('abort', abort, { once: true })
    promise.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort))
  })
}

export interface RequestOptions { signal?: AbortSignal }

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    signal: AbortSignal.timeout(30_000),
    ...init,
    headers: { Accept: 'application/json', ...init.headers },
  })

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as Partial<ApiErrorBody>
    throw new ApiError(body.detail ?? 'Request failed', response.status, body.request_id, body.code)
  }

  if (response.status === 204) return undefined as T

  return (await response.json()) as T
}

async function authenticatedRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await withSignal(accessToken(), init.signal ?? undefined)
  init.signal?.throwIfAborted()
  if (!token) throw new ApiError('Sign in is required', 401)

  return request<T>(path, {
    ...init,
    headers: { ...init.headers, Authorization: `Bearer ${token}` },
  })
}

export const blobFirstByteTimeoutMs = 180_000
export const blobIdleTimeoutMs = 90_000

async function authenticatedBlob(path: string): Promise<Blob> {
  const token = await accessToken()
  if (!token) throw new ApiError('Sign in is required', 401)
  const controller = new AbortController()
  let timer = setTimeout(() => controller.abort(), blobFirstByteTimeoutMs)
  const extend = () => {
    clearTimeout(timer)
    timer = setTimeout(() => controller.abort(), blobIdleTimeoutMs)
  }
  try {
    const response = await fetch(`${baseUrl}${path}`, {
      signal: controller.signal,
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!response.ok) throw new ApiError('Could not download result', response.status)
    if (!response.body) return await response.blob()
    const reader = response.body.getReader()
    const chunks: BlobPart[] = []
    for (;;) {
      const { done, value } = await reader.read()
      if (done) break
      extend()
      chunks.push(value)
    }
    return new Blob(chunks, { type: response.headers.get('Content-Type') ?? '' })
  } finally {
    clearTimeout(timer)
  }
}

export const api = {
  health: () => request<HealthResponse>('/health'),
  config: () => request<PublicConfigResponse>('/config'),
  catalog: () => request<CatalogResponse>('/catalog'),
  me: (options: RequestOptions = {}) => authenticatedRequest<MeResponse>('/me', options),
  completeOnboarding: () => authenticatedRequest<void>('/me/onboarding', { method: 'POST' }),
  paymentPrices: () => authenticatedRequest<PaymentPricesResponse>('/payments/prices'),
  paymentOffers: () => authenticatedRequest<PaymentOffersResponse>('/payments/offers'),
  welcomeBonus: () => authenticatedRequest<WelcomeBonusResponse>('/welcome-bonus'),
  startWelcomeBonus: () => authenticatedRequest<WelcomeBonusResponse>('/welcome-bonus/start', { method: 'POST' }),
  deleteAccount: () => authenticatedRequest<void>('/me', { method: 'DELETE' }),
  uploadMedia: (file: File) => {
    const body = new FormData()
    body.append('file', file)
    return authenticatedRequest<MediaUploadResponse>('/media', { method: 'POST', body })
  },
  createGeneration: (payload: CreateGenerationRequest, idempotencyKey: string) =>
    authenticatedRequest<GenerationResponse>('/generations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify(payload),
    }),
  generation: (jobId: string) =>
    authenticatedRequest<GenerationResponse>(`/generations/${encodeURIComponent(jobId)}`),
  generations: (family: 'video' | 'photo', cursor?: string | null, limit = 20) =>
    authenticatedRequest<GenerationListResponse>(
      `/generations?${new URLSearchParams({
        family,
        limit: String(limit),
        ...(cursor ? { cursor } : {}),
      })}`,
    ),
  deleteGeneration: (jobId: string) =>
    authenticatedRequest<void>(`/generations/${encodeURIComponent(jobId)}`, { method: 'DELETE' }),
  mediaBlob: (path: string) => authenticatedBlob(path.replace(/^\/api\/v1/, '')),
  thumbnailBlob: (jobId: string) =>
    authenticatedBlob(`/generations/${encodeURIComponent(jobId)}/thumbnail`),
  checkout: (productId: string, idempotencyKey: string, meta: { fbc?: string, fbp?: string } = {},
    options: { uiMode?: CheckoutUiMode, preview?: boolean } = {}) =>
    authenticatedRequest<CheckoutResponse>('/payments/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
      body: JSON.stringify({
        product_id: productId,
        ...meta,
        ...(options.uiMode ? { ui_mode: options.uiMode } : {}),
        ...(options.preview ? { preview: true } : {}),
      }),
    }),
  recordAttribution: (payload: AttributionRequest) =>
    authenticatedRequest<AttributionResponse>('/attribution', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }),
  subscriptionPortal: (productId?: 'weekly' | 'yearly') =>
    authenticatedRequest<CheckoutResponse>('/payments/portal', {
      method: 'POST',
      ...(productId ? {
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_id: productId }),
      } : {}),
    }),
  reconcilePortalReturn: () =>
    authenticatedRequest<PortalReturnResponse>('/payments/portal/return', { method: 'POST' }),
  confirmCheckout: (sessionId: string, options: RequestOptions = {}) =>
    authenticatedRequest<ConfirmCheckoutResponse>('/payments/confirm', {
      ...options,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId }),
    }),
}
